import {
  Accordion,
  Button,
  Form,
  TextAreaInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import { ChoiceTask } from "@validereinc/domain";
import { produce } from "immer";
import React, { useEffect, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
const { AccordionPanel } = Accordion;

type SingleChoiceType = Pick<ChoiceTask["choices"][0], "name" | "description">;

const WorkflowConfigurationSingleChoice = ({
  onUpdate,
  onIsValidUpdate,
  defaultValues,
}: {
  onUpdate: (updates: SingleChoiceType) => void;
  onIsValidUpdate: (isValid: boolean) => void;
  defaultValues: SingleChoiceType;
}) => {
  const form = useForm<SingleChoiceType>({
    defaultValues,
  });

  useEffect(() => {
    onIsValidUpdate(form.formState.isValid);
  }, [form.formState.isValid, onIsValidUpdate]);

  const handleValueChange = (key: "name" | "description", value: unknown) => {
    const formValues = form.getValues();
    onUpdate({
      ...formValues,
      [key]: value,
    });
  };

  return (
    <Form {...form}>
      <TextInput
        label="Choice Title"
        name="name"
        type="text"
        isRequired
        onChange={(value: string) => handleValueChange("name", value)}
      />
      <TextAreaInput
        label="Choice Description"
        name="description"
        isRequired
        onChange={(value: string) => handleValueChange("description", value)}
      />
    </Form>
  );
};

const WorkflowConfigurationChoice = () => {
  const { setChoice, setChoiceIsValid, getChoice } =
    useWorkflowConfigurationFormStore();

  const storeValues = getChoice();

  const [choiceValidityMap, setChoiceValidityMap] = useState<
    Record<string, boolean>
  >({});

  const areAllChoicesValid = Object.values(choiceValidityMap).every(Boolean);

  useEffect(() => {
    setChoiceIsValid(areAllChoicesValid);
  }, [areAllChoicesValid, setChoiceIsValid]);

  return (
    <Accordion defaultActiveKeys={["choices-tab"]}>
      <AccordionPanel
        dataKey={"choices-tab"}
        title="User Choices"
      >
        <Accordion>
          {(storeValues?.choices ?? []).map((choice, index) => {
            const moveUpButton = (
              <Button
                disabled={index === 0}
                icon="arrow-circle-up"
                size="small"
                iconProps={{ size: 22 }}
                onClick={() => {
                  setChoice({
                    choices: produce(storeValues?.choices ?? [], (draft) => {
                      const temp = draft[index - 1];
                      draft[index - 1] = draft[index];
                      draft[index] = temp;
                    }),
                  });
                }}
              />
            );
            const moveDownButton = (
              <Button
                disabled={(storeValues?.choices ?? []).length - 1 === index}
                icon="arrow-circle-down"
                size="small"
                iconProps={{ size: 22 }}
                onClick={() => {
                  setChoice({
                    choices: produce(storeValues?.choices ?? [], (draft) => {
                      const temp = draft[index + 1];
                      draft[index + 1] = draft[index];
                      draft[index] = temp;
                    }),
                  });
                }}
              />
            );

            return (
              <AccordionPanel
                title={`Choice ${index + 1}`}
                dataKey={choice.id}
                variant="bordered"
                actionRow={[moveUpButton, moveDownButton]}
                shouldUseAnimation={false}
                key={choice.id}
              >
                <WorkflowConfigurationSingleChoice
                  defaultValues={choice}
                  onUpdate={(state) => {
                    setChoice({
                      choices: produce(storeValues?.choices ?? [], (draft) => {
                        draft[index].name = state.name;
                        draft[index].description = state.description;
                      }),
                    });
                  }}
                  onIsValidUpdate={(isValid) => {
                    if (!!choiceValidityMap?.[choice.id] !== isValid) {
                      setChoiceValidityMap((prev) => ({
                        ...prev,
                        [choice.id]: isValid,
                      }));
                    }
                  }}
                />
              </AccordionPanel>
            );
          })}
        </Accordion>
      </AccordionPanel>
    </Accordion>
  );
};

export default WorkflowConfigurationChoice;
